import { ChangeEvent } from "react";
import { provincesMap, statesMap } from "shared";
import { FormElement, FormInput, FormSelect } from "../../../components";
import { ValidationErrors } from "../../../SettingsPage.types";
import {
  CustomerMailingAddress,
  CustomerMailingAddressKey,
} from "../EditMailingAddressPage.types";

type EditMailingAddressFormProps = {
  address: CustomerMailingAddress;
  changedFields: Set<CustomerMailingAddressKey>;
  validationErrors: ValidationErrors;
  pending: boolean;

  /** Callback when a field changes */
  onChange: (event: ChangeEvent<HTMLInputElement | HTMLSelectElement>) => void;
};

export const EditMailingAddressForm = ({
  address,
  changedFields,
  validationErrors,
  pending,
  onChange,
}: EditMailingAddressFormProps) => {
  const { country, line1, line2, city, state, postalCode } = address;
  const isUnitedStates = country === "USA";
  const states = isUnitedStates ? statesMap : provincesMap;

  const getFieldError = (id: CustomerMailingAddressKey): string | undefined => {
    return changedFields.has(id) ? validationErrors[id] : undefined;
  };

  return (
    <>
      <FormElement<CustomerMailingAddressKey>
        id="line1"
        label="Street address"
        required
        showRequiredIndicator={false}
        error={getFieldError("line1")}
      >
        <FormInput
          autoComplete="address-line1"
          maxLength={255}
          onChange={onChange}
          value={line1}
          readOnly={pending}
        />
      </FormElement>

      <FormElement<CustomerMailingAddressKey>
        id="line2"
        label="Street address 2"
        showOptionalIndicator
      >
        <FormInput
          autoComplete="address-line2"
          maxLength={160}
          onChange={onChange}
          value={line2}
          readOnly={pending}
        />
      </FormElement>

      <FormElement<CustomerMailingAddressKey>
        id="city"
        label="City"
        required
        showRequiredIndicator={false}
        error={getFieldError("city")}
      >
        <FormInput
          autoComplete="address-level2"
          maxLength={40}
          onChange={onChange}
          value={city}
          readOnly={pending}
        />
      </FormElement>

      <FormElement<CustomerMailingAddressKey>
        id="state"
        label={isUnitedStates ? "State" : "Province"}
        required
        showRequiredIndicator={false}
      >
        <FormSelect
          autoComplete="address-level1"
          value={state}
          disabled={pending}
          items={states}
          onChange={onChange}
        />
      </FormElement>

      <FormElement<CustomerMailingAddressKey>
        id="postalCode"
        label={isUnitedStates ? "Zip code" : "Postal code"}
        required
        showRequiredIndicator={false}
        error={getFieldError("postalCode")}
      >
        <FormInput
          autoComplete="postal-code"
          maxLength={20}
          onChange={onChange}
          value={postalCode}
          readOnly={pending}
          placeholder={isUnitedStates ? undefined : "A1A 1A1"}
        />
      </FormElement>

      {/* This only exists so the form can be submitted via the enter key. */}
      <input type="submit" value="Submit" style={{ display: "none" }} />
    </>
  );
};
